import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { operatorAPI } from '../../lib/api';
import { 
  Search, Filter, ChevronRight, CheckCircle, XCircle, 
  AlertTriangle, Clock, Users, RefreshCw
} from 'lucide-react';

/**
 * Operator Families List
 * Endpoints used:
 * - GET /api/operator/families
 */
export const OperatorFamilies = () => {
  const [families, setFamilies] = useState([]);
  const [filteredFamilies, setFilteredFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, configured, active, churn-risk
  const navigate = useNavigate();

  useEffect(() => {
    loadFamilies();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [families, search, filter]);

  const loadFamilies = async () => {
    try {
      setError(null);
      const response = await operatorAPI.getFamilies(200);
      setFamilies(response.data?.families || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...families];
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(f => 
        (f.family_name || '').toLowerCase().includes(searchLower) ||
        (f.id || '').toLowerCase().includes(searchLower)
      );
    }
    
    // Status filter
    if (filter === 'configured') {
      result = result.filter(f => f.is_configured);
    } else if (filter === 'active') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      result = result.filter(f => f.last_active_at && new Date(f.last_active_at) > sevenDaysAgo);
    } else if (filter === 'churn-risk') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      result = result.filter(f => 
        f.is_configured && (!f.last_active_at || new Date(f.last_active_at) < sevenDaysAgo)
      );
    }
    
    setFilteredFamilies(result);
  };

  const getStatusBadge = (family) => {
    if (!family.is_configured) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
          <Clock className="w-3 h-3" />
          Setup Incomplete
        </span>
      );
    }
    
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const isActive = family.last_active_at && new Date(family.last_active_at) > sevenDaysAgo;
    
    if (isActive) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Active
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <AlertTriangle className="w-3 h-3" />
        Churn Risk
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="families-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="operator-families-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Families</h1>
          <p className="text-slate-500 mt-1">{families.length} families total</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadFamilies}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
            data-testid="refresh-families"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <Link
            to="/onboarding"
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            data-testid="add-family-button"
          >
            <Users className="w-4 h-4" />
            Add Family
          </Link>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by family name or ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-slate-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              data-testid="filter-select"
            >
              <option value="all">All Families</option>
              <option value="configured">Configured</option>
              <option value="active">Active (7d)</option>
              <option value="churn-risk">Churn Risk</option>
            </select>
          </div>
        </div>
      </div>

      {/* Families Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="families-table">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Family</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Setup Score</th>
              <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Kids</th>
              <th className="text-center px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Caregivers</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Active</th>
              <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Last Error</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredFamilies.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                  {search || filter !== 'all' ? 'No families match your filters' : 'No families found'}
                </td>
              </tr>
            ) : (
              filteredFamilies.map((family) => (
                <tr 
                  key={family.id}
                  onClick={() => navigate(`/operator/family/${family.id}`)}
                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                  data-testid={`family-row-${family.id}`}
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-slate-900">{family.family_name || 'Unnamed Family'}</p>
                      <p className="text-xs text-slate-400 font-mono">{family.id?.slice(0, 8)}...</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(family)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex items-center justify-center">
                      <div className="w-12 h-12 relative">
                        <svg className="w-12 h-12 transform -rotate-90">
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            fill="none"
                            stroke="#e2e8f0"
                            strokeWidth="4"
                          />
                          <circle
                            cx="24"
                            cy="24"
                            r="20"
                            fill="none"
                            stroke={family.setup_score >= 60 ? '#22c55e' : family.setup_score >= 30 ? '#eab308' : '#ef4444'}
                            strokeWidth="4"
                            strokeDasharray={`${(family.setup_score || 0) * 1.256} 125.6`}
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-semibold">
                          {family.setup_score || 0}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center font-medium text-slate-700">
                    {family.children_count || 0}
                  </td>
                  <td className="px-6 py-4 text-center font-medium text-slate-700">
                    {family.caregivers_count || 0}
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-500">
                    {formatDate(family.last_active_at)}
                  </td>
                  <td className="px-6 py-4">
                    {family.last_error_stage && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-red-50 text-red-600">
                        <XCircle className="w-3 h-3" />
                        {family.last_error_stage}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Results Count */}
      {filteredFamilies.length > 0 && (
        <p className="text-sm text-slate-500 mt-4 text-center">
          Showing {filteredFamilies.length} of {families.length} families
        </p>
      )}
    </div>
  );
};

export default OperatorFamilies;
